import type { ApiResponse } from "@/types/common";
import { UserStatus } from "@/types/userStatus";
import { privateRequest } from "../axios";
import type { AxiosResponse } from "axios";

export interface UserSocialLink {
  instagramProfileUrl: string | null;
  linkedinProfileUrl: string | null;
}

export interface UserSettings {
  isPushNotificationEnabled?: boolean;
  language?: string | null;
}

export interface NotificationLog {
  id?: number | string;
  title?: string | null;
  subject?: string | null;
  name?: string | null;
  message?: string | null;
  description?: string | null;
  body?: string | null;
  content?: string | null;
  text?: string | null;
  type?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isRead?: boolean | null;
  read?: boolean | null;
  [key: string]: unknown;
}

export interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string | null;
  location: string | null;
  profileUrl: string | null;
  isActive: boolean;
  companyName: string | null;
  companyWebsite: string | null;
  designation: string | null;
  gender: string | null;
  dob: string | null;
  status: UserStatus | null;
  statusRemark: string | null;
  userType: string | null;
  socialLink: UserSocialLink | null;
  userSettings?: UserSettings | null;
  city?: number | string | null;
  currentFocus?: string | null;
  fundingRound?: number | string | null;
  sector?: number | string | null;
  helpTopics?: string[] | null;
}

export interface UpdateUserPayload {
  firstName?: string;
  lastName?: string;
  mobileNo?: string | null;
  location?: string | null;
  companyName?: string | null;
  companyWebsite?: string | null;
  designation?: string | null;
  gender?: string | null;
  dob?: string | null;
  userType?: string | null;
  socialLink?: UserSocialLink | undefined;
  userSettings?: UserSettings;
  city?: number | string | null;
  currentFocus?: string | null;
  fundingRound?: number | string | null;
  sector?: number | string | null;
  helpTopics?: string[] | null;
}

const ENDPOINTS = {
  PROFILE_UPDATE: "/users",
  PROFILE_ME: "/users/me",
  PROFILE_IMAGE: "/users/profile/image",
  NOTIFICATION_LOG: "/notification-logs",
  NOTIFICATION_LOGS_COUNT: "/notification-logs/count",
  NOTIFICATION_LOGS_MARK_AS_READ: "/notification-logs/mark-as-read",
};

const getUserProfile = async (): Promise<ApiResponse<UserProfile>> => {
  const response: AxiosResponse<ApiResponse<UserProfile>> =
    await privateRequest.get(ENDPOINTS.PROFILE_ME);
  return response.data;
};

const updateUserProfile = async (
  data: UpdateUserPayload,
): Promise<ApiResponse<UserProfile>> => {
  const response: AxiosResponse<ApiResponse<UserProfile>> =
    await privateRequest.put(ENDPOINTS.PROFILE_UPDATE, data);
  return response.data;
};

const updateUserProfileImage = async (
  file: File | null,
): Promise<ApiResponse<UserProfile>> => {
  const formData = new FormData();

  if (file) {
    formData.append("file", file);
  } else {
    // Postman "file: null" sends an empty file part, not a text empty string.
    formData.append(
      "file",
      new File([], "null", { type: "application/octet-stream" }),
    );
  }

  const response: AxiosResponse<ApiResponse<UserProfile>> =
    await privateRequest.put(ENDPOINTS.PROFILE_IMAGE, formData);
  return response.data;
};

const getNotificationLogs = async (): Promise<ApiResponse<NotificationLog[]>> => {
  const response: AxiosResponse<ApiResponse<NotificationLog[]>> =
    await privateRequest.get(ENDPOINTS.NOTIFICATION_LOG);
  return response.data;
};

const getNotificationLogsCount = async (): Promise<ApiResponse<{ count: number } | number>> => {
  const response: AxiosResponse<ApiResponse<{ count: number } | number>> =
    await privateRequest.get(ENDPOINTS.NOTIFICATION_LOGS_COUNT);
  return response.data;
};

const getNotificationLogsMarkAsRead = async (): Promise<ApiResponse<null>> => {
  const response: AxiosResponse<ApiResponse<null>> =
    await privateRequest.put(ENDPOINTS.NOTIFICATION_LOGS_MARK_AS_READ);
  return response.data;
};

const deleteUserAccount = async (
  id: number | string,
): Promise<ApiResponse<null>> => {
  const response = await privateRequest.delete<ApiResponse<null>>(
    `${ENDPOINTS.PROFILE_UPDATE}/${id}`,
  );
  return response.data;
};

const UserService = {
  getUserProfile,
  updateUserProfile,
  updateUserProfileImage,
  deleteUserAccount,
  getNotificationLogs,
  getNotificationLogsCount,
  getNotificationLogsMarkAsRead,
};

export {
  getUserProfile,
  updateUserProfile,
  updateUserProfileImage,
  deleteUserAccount,
  getNotificationLogs,
  getNotificationLogsCount,
  getNotificationLogsMarkAsRead,
};
export default UserService;
